import { LoginUserBasicResponse } from "@microtica/ms-usermanagement-sdk";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { getUserManagementService } from "../../backend";
import { LoginChallenge, securityApi, toLoginChallenge } from "../../backend/security";
import { useAuth } from "../../contexts/Auth";

// A backend without identifier-first sign-in (prod until the MFA release) answers
// /users/login-challenges with a plain nginx error instead of a JSON error code.
// That error page has no CORS headers, so the browser usually reports it as a network error.
function isLoginFlowUnsupported(error: any): boolean {
    if (!error?.response && error?.code === "ERR_NETWORK" && navigator.onLine) {
        return true;
    }
    const status = error?.response?.status;
    const data = error?.response?.data;
    return [404, 405, 500, 501, 502].includes(status) && !(data && typeof data === "object" && "code" in data);
}

export interface SignInError {
    title: string;
    /** Offer both recovery paths: reset the password, or resend the activation email */
    offerRecovery: boolean;
    hint?: string;
}

/** The sign-in flow behind the login page, shared by the classic and the new UI's views. */
export function useLogin() {
    const postMfaRedirect = sessionStorage.getItem("postMfaRedirect") || undefined;
    const { isLoggedIn, loginWithExternalProvider, processAuthData } = useAuth({ redirectUrl: postMfaRedirect });
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [mfaChallenge, setMfaChallenge] = useState<LoginChallenge>();
    const [email, setEmail] = useState("");
    // With PreventUserExistenceErrors ENABLED, Cognito stops telling us WHY a
    // sign-in cannot start, so the screen offers both recovery paths instead of
    // detecting which one the user needs.
    const [signInError, setSignInError] = useState<SignInError>();
    // Classic email + password sign-in, used when the backend has no identifier-first flow
    const [passwordStep, setPasswordStep] = useState(false);

    useEffect(() => {
        if (isLoggedIn) {
            // A step-up sign-in returns the user to the project that asked for it.
            navigate(postMfaRedirect || "/projects");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    useEffect(() => {
        const savedChallenge = sessionStorage.getItem("loginMfaChallenge");
        if (!savedChallenge) {
            return;
        }
        sessionStorage.removeItem("loginMfaChallenge");
        try {
            setMfaChallenge(JSON.parse(savedChallenge) as LoginChallenge);
        } catch {
            // A malformed or expired browser value should not block sign-in.
        }
    }, []);

    async function handleLoginResult(data: any, username: string) {
        const challenge = data as Partial<LoginChallenge> & { done?: boolean; availableChallenges?: string[] };
        const { done, challengeName } = challenge;

        if (!done && challengeName === "NEW_PASSWORD_REQUIRED") {
            sessionStorage.setItem("passwordChallenge", JSON.stringify({
                username,
                // The server never returns a Cognito Session, only an opaque transaction id.
                transactionId: challenge.transactionId
            }));
            navigate(`/auth/set-password/${encodeURIComponent(username)}`);
        } else if (!done) {
            const mfaChallenge = toLoginChallenge(challenge, username);
            if (!mfaChallenge) {
                throw new Error("The sign-in challenge was missing its transaction id.");
            }
            setMfaChallenge(mfaChallenge);
        } else {
            await processAuthData(data as LoginUserBasicResponse, "email");
        }
    }

    async function handlePasswordLogin(values: { email: string; password: string }) {
        try {
            setLoading(true);
            setSignInError(undefined);
            const { data } = await getUserManagementService().loginUser({ username: values.email, password: values.password });
            if ((data as any).done === false) {
                setSignInError({ title: "This account needs a new password. Set it in the Microtica portal first.", offerRecovery: false });
                return;
            }
            await processAuthData(data as LoginUserBasicResponse, "email");
        } catch (error: any) {
            const status = error?.response?.status;
            // Only an answer from the sign-in service can say the password is wrong; an outage or a
            // dropped connection must never read as one
            if (!status) {
                setSignInError({ title: "We couldn't reach Microtica", offerRecovery: false, hint: "Check your connection and try again." });
            } else if (status === 429 || /attempts exceeded/i.test(error?.response?.data?.message || "")) {
                setSignInError({ title: "Too many attempts", offerRecovery: false, hint: "Wait a few minutes before trying again." });
            } else if (status >= 500) {
                setSignInError({ title: "Something went wrong on our side", offerRecovery: false, hint: "Please try again in a moment." });
            } else {
                setSignInError({ title: "Email or password is incorrect", offerRecovery: false, hint: "Signed up with GitHub or Google? Use that instead." });
            }
        } finally {
            setLoading(false);
        }
    }

    async function handleLogin(values: { email: string; password?: string }) {
        if (passwordStep) {
            return handlePasswordLogin({ email: values.email, password: values.password || "" });
        }
        try {
            setLoading(true);
            setSignInError(undefined);
            const data = await securityApi.startLogin(values.email);
            await handleLoginResult(data, values.email);

        } catch (error: any) {
            if (isLoginFlowUnsupported(error)) {
                setPasswordStep(true);
                return;
            }
            const code = error?.response?.data?.code;
            // These two only arrive while the pool still discloses account state.
            // They are kept so the routing stays sharp if that is ever reverted.
            if (code === "USER_NOT_CONFIRMED") {
                navigate(`/auth/activate-account?email=${encodeURIComponent(values.email)}`);
                return;
            }
            if (code === "PASSWORD_RESET_REQUIRED") {
                navigate(`/auth/reset-password?email=${encodeURIComponent(values.email)}`);
                return;
            }
            if (code === "RATE_LIMITED") {
                setSignInError({ title: "Too many attempts", offerRecovery: false, hint: "Wait a few minutes before trying again." });
                return;
            }
            setSignInError({ title: "We couldn't continue with that email address", offerRecovery: true });
        } finally {
            setLoading(false);
        }
    }

    // Once an account is identified the header stops giving instructions and
    // confirms who is signing in, so the user can catch a wrong address early.
    const challengeAccount = mfaChallenge?.username || email;
    const subtitle = !mfaChallenge
        ? "Enter your email to continue"
        : challengeAccount || "Confirm it's you to continue";

    /** Keeps the typed address on the recovery links, so the next page starts filled in */
    const withEmail = (path: string) => `${path}${email ? `?email=${encodeURIComponent(email)}` : ""}`;

    return {
        email,
        setEmail,
        loading,
        mfaChallenge,
        signInError,
        setSignInError,
        passwordStep,
        subtitle,
        withEmail,
        submit: handleLogin,
        /** Hands a challenge answer back to the flow: a finished sign-in, or the next step */
        continueChallenge: (result: any) => handleLoginResult(result, mfaChallenge?.username || email),
        cancelChallenge: () => setMfaChallenge(undefined),
        loginWithGitHub: () => loginWithExternalProvider("github", "login"),
        loginWithGoogle: () => loginWithExternalProvider("google", "login"),
    };
}
